import { useRef, useState } from 'react';
import { message } from '$/components/Message';
import localStorageUtil from '$/common/utils/localStorageUtil';
import { buildPopupData } from '$/utils/messageUtil';
import type { IInlineBoxData } from '$/components/InlineBox/types';
import type { ViralRemakeRunTrackParams } from '$/domains/viralRemake/types';
import {
  createAgentTeamRoom as requestCreateAgentTeamRoom,
  getMediaAnalysis,
  type CreateAgentTeamRoomRequestParams,
} from '$/domains/teamAgent/teamAgentService';
import { createTeamCanvas } from '$/domains/workFlowV3/teamCanvasService';
import { createCanvasV3 } from '$/domains/workFlowV3/workFlowV3Service';
import { notifyMyIpChanged } from '$/domains/myIp/changeEvents';
import { bindMyIpsToProject } from '$/domains/myIp/myIpService';
import { useOfficialStore } from '$/store/useOfficialStore';
import { InputPromptSkillItem } from '$/domains/caseV3/type';
/* import { forkCanvasDataV3 } from '$/domains/workFlowV3/workFlowV3Service'; */
import { mtstatTrack } from '$/utils/mtstat';
import {
  isTeamStudioInternalTrackLocation,
  resolveTeamProjectCreateLocation,
  trackTeamConversationCreated,
  trackTeamProjectCreated,
} from '$/domains/workFlowV3/teamTracking';

export interface CreateAgentTeamRoomParams {
  prompt?: string;
  markElementList?: IInlineBoxData[];
  skillList?: InputPromptSkillItem[];
  usedModel?: any;
  activeMode?: string;
  agentInfoList?: Record<string, any>[];
  agentThinkingModeType?: string;
  isAutoSubmit?: boolean;
  cardId?: string;
  trackLocation: string;
  trackType?: string | null;
  caseId?: string;
  withCanvasData?: boolean;
  /** 新建项目时所在的文件夹；根目录或不传 folder_id 时由服务端默认处理。 */
  folderId?: string | number;
  /** 新建项目后需要绑定的 IP ID；全部绑定成功后才进入项目。 */
  ip_ids?: string[];
  /** 调用页面已切换或卸载时停止后续绑定与导航。 */
  isCurrent?: () => boolean;
  /** IP 完成链按成功阶段恢复；未传入时保留普通入口每次新建的行为。 */
  completion?: {
    getProgress: () => AgentTeamRoomProgress;
    onProgress: (progress: AgentTeamRoomProgress) => void;
    navigate: (target: AgentTeamRoomResult) => Promise<unknown>;
  };
  /** 团队项目创建埋点使用的当前文件夹名称；根目录按 `/`。 */
  trackFolderName?: string;
  /** 是否把未被 text 引用的资源拼接到 text 前，默认保留旧首页兼容行为。 */
  autoPrefixUnreferencedResources?: boolean;
  /** 仅在创建房间时透传的 Agent 配置。 */
  roomCreateParams?: CreateAgentTeamRoomRequestParams;
  /** 仅创建专属会话，供 IP 完成后再建项目；不触发项目绑定、消息交接或跳转。 */
  roomOnly?: boolean;
  /** 进入房间后的自动发消息配置；requestParams 透传 Chat 顶层字段。 */
  autoSendMessage?: {
    requestParams?: Record<string, any>;
    /** 正常请求 Chat，但不向消息列表插入用户消息。 */
    skipUserMessage?: boolean;
  };
  /** 爆款复刻自动发送的 run_click 参数，目标会话取得任务 ID 后才真正上报。 */
  viralRemakeRunTrack?: ViralRemakeRunTrackParams;
}

export interface AgentTeamRoomResult {
  roomId: string;
  projectId?: string;
  canvasId?: string;
}

export interface AgentTeamRoomProgress {
  roomId?: string;
  projectId?: string;
  canvasId?: string;
  ipIds?: string[];
  tracked?: boolean;
}

/**
 * 判断当前是否处于团队态。
 */
function isTeamEditionActive() {
  return Boolean(useOfficialStore.getState().clientOId);
}

/**
 * 将 Agent 房间登记为项目：团队版走 /team/canvas/create，个人版走 /v3/canvas/create。
 */
async function bindProjectForEdition(
  roomId: string,
  options?: {
    folderId?: string | number;
    caseId?: string;
  },
) {
  const teamEditionActive = isTeamEditionActive();
  const project = teamEditionActive
    ? await createTeamCanvas({
      room_id: roomId,
      folder_id: options?.folderId,
      case_id: options?.caseId,
    })
    : await createCanvasV3({
      room_id: roomId,
      case_id: options?.caseId,
    });
  const resolvedProjectId = project?.project_id ? String(project.project_id) : '';
  const resolvedCanvasId = project?.canvas_id ? String(project.canvas_id) : '';
  if (!resolvedProjectId || !resolvedCanvasId) {
    throw new Error('Incomplete canvas binding response');
  }
  return {
    roomId,
    projectId: resolvedProjectId,
    canvasId: resolvedCanvasId,
  };
}

/**
 * 统一创建 AgentTeam 房间；默认绑定项目并跳转，专属会话可由调用方继续后续业务流程。
 */
export default function useCreateRoom({
  onLoginSuccessed,
  createAfterLogin = false,
}: {
  onLoginSuccessed?: () => Promise<void> | void;
  createAfterLogin?: boolean;
}) {
  const navigateMap = useOfficialStore((state) => state.navigateMap);
  const toLogin = useOfficialStore((state) => state.toLogin);
  const isCreatingRef = useRef(false);
  const [loading, setLoading] = useState(false);

  /**
   * 未登录时拉起登录；指定 createAfterLogin 的入口在登录成功后恢复原创建动作。
   */
  function ensureLogin(onLoggedIn?: () => Promise<void> | void) {
    const isUserLoggedIn = useOfficialStore.getState().isUserLoggedIn();
    if (isUserLoggedIn) {
      return true;
    }
    toLogin()
      .then(async () => {
        try {
          if (onLoginSuccessed) {
            await onLoginSuccessed();
          }
        } catch (error) {
          console.error('onLoginSuccessed error', error);
        }
        if (onLoggedIn) {
          await onLoggedIn();
        }
      })
      .catch(() => {});
    return false;
  }

  /**
   * 统一处理登录失效，保持与房间创建失败逻辑一致。
   */
  function handleLoginExpired() {
    message.error('RoboNeo_Web_login_status_fail');
    setTimeout(() => window.location.reload(), 1000);
  }

  /**
   * 创建 AgentTeam 房间；首页创作入口取得房间 ID 即上报，后续项目绑定重试复用成功进度。
   */
  async function createAgentTeamRoom(params: CreateAgentTeamRoomParams): Promise<AgentTeamRoomResult | undefined> {
    if (isCreatingRef.current || params.isCurrent?.() === false) {
      return undefined;
    }

    isCreatingRef.current = true;
    // 绑定成功但未能离开当前页时，再补广播；成功跳转不再拉 summary，避免 Safari abort 误报。
    let boundIps = false;

    try {
      const hasLoggedIn = ensureLogin(
        createAfterLogin ? async () => {
          await createAgentTeamRoom(params);
        } : undefined,
      );
      if (!hasLoggedIn) {
        return undefined;
      }
      setLoading(true);
      const { completion } = params;
      let progress = completion?.getProgress() || {};
      // 上次写存储失败时先补存内存中的成功进度，再执行下一步请求。
      completion?.onProgress(progress);
      if (params.isCurrent?.() === false) return undefined;
      let targetRoomId = progress.roomId;
      if (!targetRoomId) {
        const response = await requestCreateAgentTeamRoom(params.roomCreateParams);
        if (!completion && params.isCurrent?.() === false) return undefined;
        const errorCode = response?.data?.code;
        const roomId = response?.data?.result?.room_id;

        if (errorCode === 5001) {
          if (params.isCurrent?.() !== false) handleLoginExpired();
          return undefined;
        }

        // 专属会话的 source_session_id 必须保留服务端原始字符串，不能把无效值强转成身份。
        if (errorCode !== 0 || !roomId || (params.roomOnly && (typeof roomId !== 'string' || !roomId.trim()))) {
          if (params.isCurrent?.() !== false) message.error('RoboNeo_Web_chat_send_fail');
          return undefined;
        }

        targetRoomId = String(roomId);
        progress = { ...progress, roomId: targetRoomId };
        // 调用方负责隔离已失效的账号空间；成功响应先交给它保留，再停止旧上下文。
        completion?.onProgress(progress);
        if (params.isCurrent?.() === false) return undefined;
      }

      // IP 专属会话不应提前生成独立项目，也不能覆盖当前页面的消息交接与导航。
      if (params.roomOnly) return { roomId: targetRoomId };

      // 首页创作入口按房间创建成功统计，不能因后续项目创建或 IP 绑定失败漏报。
      if (!progress.tracked && ['home_viral_remake', 'home_ai_video', 'home_emoji', 'home_short_video'].includes(params.trackLocation)) {
        mtstatTrack('create_conversation', {
          location: params.trackLocation,
          room_id: targetRoomId,
          type: params.trackType ?? 'agent_teams',
        });
        progress = { ...progress, tracked: true };
        completion?.onProgress(progress);
      }

      let { projectId, canvasId } = progress;
      const teamEditionActive = isTeamEditionActive();

      // TODO: 临时关闭 fork，待新 fork 接口联调后恢复（ROB-11036）
      // if (params.withCanvasData) {
      //   try {
      //     await forkCanvasDataV3({
      //       room_id: targetRoomId,
      //       case_id: params.caseId ? Number(params.caseId) : undefined,
      //     });
      //   } catch (e) {
      //     console.error('forkCanvasDataV3 error', e);
      //     message.error('数据获取失败');
      //     return { roomId: targetRoomId, projectId };
      //   }
      // }
      // 已建房的重试也必须准备消息：前次存储可能失败，调用方也可能已清理失败的交接。
      if ((params.markElementList && params.markElementList.length > 0) || params.prompt || params.autoSendMessage) {
        const { markElementList = [] } = params;
        const text = params.prompt ?? '';
        const popupPayload = buildPopupData({
          room_id: targetRoomId,
          text,
          markElementList,
          usedModel: params.usedModel,
          activeMode: params.activeMode,
          agentInfoList: params.agentInfoList,
          agentThinkingModeType: params.agentThinkingModeType,
          skillList: params.skillList,
          isAutoSubmit: params.isAutoSubmit ?? true,
          cardId: params.cardId,
          autoPrefixUnreferencedResources: params.autoPrefixUnreferencedResources,
          autoSendMessage: params.autoSendMessage,
          viralRemakeRunTrack: params.viralRemakeRunTrack,
        });
        localStorageUtil.setItem('popupData', JSON.stringify(popupPayload));

        const mediaList = markElementList
          .filter((item) => item.mediaType === 'image' || item.mediaType === 'video')
          .map((item) => ({
            media_url: item.url,
            index: item.index || '',
            media_type: item.mediaType as 'image' | 'video',
          }));
        if (mediaList.length > 0) {
          getMediaAnalysis({ room_id: targetRoomId, media_list: mediaList }).catch(() => {});
        }
      }

      try {
        if (!projectId || !canvasId) {
          const boundProject = await bindProjectForEdition(targetRoomId, {
            folderId: params.folderId,
            caseId: params.caseId,
          });
          projectId = boundProject.projectId;
          canvasId = boundProject.canvasId;
          progress = { ...progress, projectId, canvasId };
          completion?.onProgress(progress);
        }
      } catch (error) {
        console.error('bind project for edition error:', error);
        if (params.isCurrent?.() !== false) message.error('RoboNeo_Web_chat_send_fail');
        return undefined;
      }

      if (params.isCurrent?.() === false) return undefined;
      const pendingIpIds = completion
        ? [...new Set(params.ip_ids || [])].filter((id) => !progress.ipIds?.includes(id))
        : params.ip_ids;
      if (pendingIpIds?.length) {
        try {
          await bindMyIpsToProject(projectId, pendingIpIds, undefined, { notify: false });
          boundIps = true;
          progress = { ...progress, ipIds: [...new Set([...(progress.ipIds || []), ...pendingIpIds])] };
          completion?.onProgress(progress);
        } catch (error) {
          console.error('bind IPs to project error:', error);
          if (params.isCurrent?.() !== false) message.error('RoboNeo_Web_chat_send_fail');
          return undefined;
        }
      }

      if (params.isCurrent?.() === false) return undefined;
      const trackLocation = params.trackLocation ?? 'home_page';
      const trackType = params.trackType === null
        ? 'agent_teams'
        : params.trackType ?? 'agent_teams';
      const teamProjectCreateLocation = teamEditionActive
        ? resolveTeamProjectCreateLocation(trackLocation)
        : null;

      if (!progress.tracked) {
        if (
          !isTeamStudioInternalTrackLocation(trackLocation)
        && teamProjectCreateLocation
        ) {
          trackTeamConversationCreated({
            location: teamProjectCreateLocation,
            projectId,
            roomId: targetRoomId,
            folderName: params.trackFolderName,
            type: trackType,
          });
          if (projectId) {
            trackTeamProjectCreated({
              location: teamProjectCreateLocation,
              projectId,
              roomId: targetRoomId,
              folderName: params.trackFolderName,
            });
          }
        } else if (!isTeamStudioInternalTrackLocation(trackLocation)) {
          mtstatTrack('create_conversation', {
            location: trackLocation,
            type: trackType,
            room_id: targetRoomId,
          });
        }
        progress = { ...progress, tracked: true };
        completion?.onProgress(progress);
      }
      if (params.isCurrent?.() === false) return undefined;
      const result = { roomId: targetRoomId, projectId, canvasId };
      if (completion) {
        const navigated = await completion.navigate(result);
        if (params.isCurrent?.() === false) return undefined;
        if (navigated === false) throw new Error('IP project navigation cancelled');
      } else {
        navigateMap.historyTeamFlow({
          room_id: targetRoomId,
          project_id: projectId,
          canvas_id: canvasId,
          case_id: params.caseId,
        });
      }
      return result;
    } catch (error) {
      if (boundIps) notifyMyIpChanged();
      if (params.isCurrent?.() !== false) message.error('RoboNeo_Web_chat_send_fail');
      return undefined;
    } finally {
      isCreatingRef.current = false;
      setLoading(false);
    }
  }

  return {
    loading,
    createRoom: createAgentTeamRoom,
  };
}
